import "./index.scss";
import Section from "@components/Section";
import type { CharacterAssetRecord, UserAssetV2Record } from "@apis/http/api";
import { useAppSelector } from "@hooks/redux";
import UIVideoCard from "./UIVideoCard";
import UIScriptCard from "./UIScriptCard";
import UIProductCard from "./UIProductCard";
import UIVideoGenerateCard from "./UIVideoGenerateCard";
import UIUploadedVideoList from "./UIUploadedVideoList";
import { UIImageCard } from "./UIImageCard";
import { UICreatedProductCard } from "./UICreatedProductCard";
import { CreateProductEntityCard } from "./CreateProductEntityCard";
import { CreateCharacterEntityCard } from "./CreateCharacterEntityCard";
import { UICharacterCard } from "./UICharacterCard";
import { useLibraryCards } from "../hooks/useLibraryCards";
import { useCharacterBindingPoll } from "../hooks/useCharacterBindingPoll";
import type { UseImageBatchSelectionReturn } from "../hooks/useImageBatchSelection";
import { Trans } from "react-i18next";
import { useEffect } from "react";

interface UICardWrapProps {
  onVideoDetailClick?: (record: UserAssetV2Record, index: number) => void;
  onGeneratedItemsChange?: (items: UserAssetV2Record[]) => void;
  onGeneratedRefreshChange?: (refresh: () => void) => void;
  onGeneratedRemoveItemChange?: (removeItem: (predicate: (item: UserAssetV2Record) => boolean) => void) => void;
  imageSelection?: UseImageBatchSelectionReturn;
  onImageItemsChange?: (items: UserAssetV2Record[]) => void;
  onImageRefreshChange?: (refresh: () => void) => void;
  videoSelection?: UseImageBatchSelectionReturn;
  onUploadedVideoItemsChange?: (items: UserAssetV2Record[]) => void;
  onUploadedVideoRefreshChange?: (refresh: () => void) => void;
}

export const UICardWrap = ({
  onVideoDetailClick,
  onGeneratedItemsChange,
  onGeneratedRefreshChange,
  onGeneratedRemoveItemChange,
  imageSelection,
  onImageItemsChange,
  onImageRefreshChange,
  videoSelection,
  onUploadedVideoItemsChange,
  onUploadedVideoRefreshChange,
}: UICardWrapProps) => {
  const { focusType, videoType, productType, characterType } = useAppSelector((state) => state.libraryReducer);
  const {
    items,
    status,
    loadingMore,
    hasMore,
    sentinelRef,
    refresh,
    generatedItems,
    generatedRefresh,
    generatedRemoveItem,
  } = useLibraryCards({
    focusType,
    videoType,
    productType,
    characterType,
  });

  useEffect(() => {
    onGeneratedItemsChange?.(generatedItems);
  }, [generatedItems, onGeneratedItemsChange]);

  useEffect(() => {
    onGeneratedRefreshChange?.(generatedRefresh);
  }, [generatedRefresh, onGeneratedRefreshChange]);

  useEffect(() => {
    onGeneratedRemoveItemChange?.(generatedRemoveItem);
  }, [generatedRemoveItem, onGeneratedRemoveItemChange]);

  useEffect(() => {
    onImageRefreshChange?.(refresh);
  }, [refresh, onImageRefreshChange]);

  useEffect(() => {
    if (focusType === "image") {
      onImageItemsChange?.(items as UserAssetV2Record[]);
    }
  }, [focusType, items, onImageItemsChange]);

  const isCreatedProduct = focusType === "product" && productType === "created";
  const isCharacterTab = focusType === "character";
  const isUploadedVideo = focusType === "video" && videoType === "uploaded";

  useCharacterBindingPoll(isCharacterTab ? (items as CharacterAssetRecord[]) : [], refresh, isCharacterTab);

  // UIUploadedVideoList self-manages its own loading/blank state; pass through "succeeded" for the outer Section
  const sectionStatus = isUploadedVideo
    ? "succeeded"
    : (isCreatedProduct || isCharacterTab) && status === "blank"
      ? "succeeded"
      : status;

  return (
    <Section className="min-h-[300px]" status={sectionStatus} reload={refresh}>
      {focusType === "script" && (
        <div className="grid grid-cols-3 gap-[16px]">
          {items.map((item) => (
            <UIScriptCard key={item.id} record={item} onRefresh={refresh}></UIScriptCard>
          ))}
        </div>
      )}
      {focusType === "video" && (
        <>
          {(videoType === "generated" || videoType === "saved") && (
            <div className="grid grid-cols-5 min-[1920px]:grid-cols-6 gap-[16px]">
              {videoType === "generated" &&
                items.map((item, index) => (
                  <UIVideoGenerateCard
                    key={item.id}
                    record={item}
                    onDetailClick={(record) => onVideoDetailClick && onVideoDetailClick(record, index)}
                    onRefresh={refresh}
                    selected={videoSelection?.isSelected(item.id)}
                    onSelectedChange={(sel) => videoSelection?.setSelected(item.id, sel)}
                  />
                ))}
              {videoType === "saved" &&
                items.map((item) => <UIVideoCard key={item.id} record={item} onRefresh={refresh}></UIVideoCard>)}
            </div>
          )}
          {isUploadedVideo && (
            <UIUploadedVideoList
              enabled={isUploadedVideo}
              videoSelection={videoSelection}
              onItemsChange={onUploadedVideoItemsChange}
              onRefreshChange={onUploadedVideoRefreshChange}
            />
          )}
        </>
      )}
      {focusType === "image" && (
        <div className="grid grid-cols-5 min-[1920px]:grid-cols-6 gap-[16px]">
          {items.map((item) => (
            <UIImageCard
              key={item.id}
              record={item}
              onRefresh={refresh}
              selected={imageSelection?.isSelected(item.id)}
              onSelectedChange={(selected) => imageSelection?.setSelected(item.id, selected)}
            />
          ))}
        </div>
      )}
      {focusType === "product" && (
        <div className="grid grid-cols-5 min-[1920px]:grid-cols-6 gap-[16px]">
          {productType === "created" ? (
            <>
              <CreateProductEntityCard onCreated={refresh} />
              {items.map((item) => (
                <UICreatedProductCard key={item.id} record={item} onRefresh={refresh} />
              ))}
            </>
          ) : (
            items.map((item) => <UIProductCard key={item.id} record={item} onRefresh={refresh} />)
          )}
        </div>
      )}
      {focusType === "character" && (
        <div className="grid grid-cols-5 min-[1920px]:grid-cols-6 gap-[16px]">
          <CreateCharacterEntityCard characterType={characterType} existingCount={items.length} onCreated={refresh} />
          {items.map((item) => (
            <UICharacterCard key={item.id} record={item} onRefresh={refresh} />
          ))}
        </div>
      )}
      {hasMore && <div ref={sentinelRef} className="h-px" />}
      {loadingMore && (
        <div className="py-[16px] text-center text-[14px] text-kds-fg-subtle">
          <Trans>Loading...</Trans>
        </div>
      )}
    </Section>
  );
};
